import React, { useLayoutEffect, useRef } from 'react';
import { maskPhone } from '../utils/maskUtils';

type Props = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'onKeyDown'> & {
  value: string;
  onValueChange: (digits: string) => void;
};

// Keep the cursor next to the edited digit instead of moving it to the end.
export const PhoneInput: React.FC<Props> = ({ value, onValueChange, ...props }) => {
  const pendingCursor = useRef<{ input: HTMLInputElement; position: number } | null>(null);
  useLayoutEffect(() => {
    const pending = pendingCursor.current;
    if (pending && document.activeElement === pending.input) pending.input.setSelectionRange(pending.position, pending.position);
    pendingCursor.current = null;
  });
  const update = (input: HTMLInputElement, next: string, digitPosition: number) => {
    const formatted = maskPhone(next);
    let position = formatted.startsWith('(') ? 1 : 0;
    let count = 0;
    for (let index = 0; index < formatted.length && count < digitPosition; index++) {
      if (/\d/.test(formatted[index])) { count++; position = index + 1; }
    }
    pendingCursor.current = { input, position };
    onValueChange(formatted.replace(/\D/g, ''));
  };
  return <input {...props} type="tel" value={maskPhone(value)} onChange={event => {
    const input = event.currentTarget;
    update(input, input.value, input.value.slice(0, input.selectionStart ?? input.value.length).replace(/\D/g, '').length);
  }} onKeyDown={event => {
    if (!['Backspace', 'Delete'].includes(event.key) || event.ctrlKey || event.metaKey || event.altKey) return;
    const input = event.currentTarget;
    const start = input.selectionStart ?? 0, end = input.selectionEnd ?? start;
    const digits = input.value.replace(/\D/g, '');
    const before = input.value.slice(0, start).replace(/\D/g, '').length;
    const after = input.value.slice(0, end).replace(/\D/g, '').length;
    event.preventDefault();
    if (start !== end) { update(input, digits.slice(0, before) + digits.slice(after), before); return; }
    const index = event.key === 'Backspace' ? before - 1 : before;
    if (index < 0 || index >= digits.length) return;
    update(input, digits.slice(0, index) + digits.slice(index + 1), index);
  }} />;
};
